
import { Component, Vue } from 'vue-property-decorator'
import AdminClientForm from '@/components/admin/AdminClientForm.vue'
import AdminClientWorkspacesForm from '@/components/admin/AdminClientWorkspacesForm.vue'
import AdminWorkspaces from '@/components/admin/AdminWorkspacesFromClients.vue'
import AdminDomainForm from '@/components/admin/AdminDomainForm.vue'
import TopToolBarPaginator from '@/components/generic/TopToolBarPaginator.vue'
import ImportFile from '@/components/generic/ImportFile.vue'
import catalogsService from '@/services/CatalogsService'
import { CrudActions, CatalogGridItemColumn, ClientCatalogFilters, ClientCatalogGridItem, Client, ClientStatus, Domain, DomainCatalogFilters, Workspace, ClientWorkspacesFilters } from '@/types/Catalogs'
import { PageRequest } from '@/types/Support'
import userInfoStore from '@/store/userInfo'
import { PermissionNames, RoleNames } from '@/types/Auth'
import moment from 'moment'
import reportsService from '@/services/ReportsService'
import { ErrorImport, ImportErrorCode } from '@/types/Errors'
import { ErrorTitles, ImportationErrorAdvices } from '@/types/ErrorDictionary'
import DateFormat from '@/services/DateFormat'
import LogService from '@/services/support/LogService'
import { MessageError, MessageSuccess } from '@/components/generic/MessageHandling.vue'

@Component({
  components: {
    AdminClientForm,
    AdminClientWorkspacesForm,
    AdminDomainForm,
    ImportFile,
    TopToolBarPaginator,
    AdminWorkspaces
  }
})
export default class AdminClients extends Vue {
  PAGE_SIZE = 500
  PAGE_SIZE_VISIBLE_ITEMS = 50
  itemsVisibleRange = [0, this.PAGE_SIZE_VISIBLE_ITEMS]
  paginatorKey = 0
  crudActions = CrudActions
  currentAction = CrudActions.READ
  loadingData = false
  leftSidebarCollapsed = false
  // Grid columns definition
  columns: CatalogGridItemColumn[] = [
    CatalogGridItemColumn.def('clientCode', 'Código cliente', true),
    CatalogGridItemColumn.def('id', 'ID', true),
    CatalogGridItemColumn.def('name', 'Razón social', true),
    CatalogGridItemColumn.def('statusName', 'Estado', true),
    CatalogGridItemColumn.def('rfc', 'RFC', true),
    CatalogGridItemColumn.def('juridicalPerson', 'Personalidad jurídica', true),
    CatalogGridItemColumn.def('stpClabe', 'Clabe Stp'),
    CatalogGridItemColumn.def('contactName', 'Nombre contacto', true),
    CatalogGridItemColumn.def('contactEmail', 'Correo para envío de factura'),
    CatalogGridItemColumn.def('contactPhone', 'Teléfono de facturación'),
    CatalogGridItemColumn.def('contactEmailExtra', 'Correo adicional'),
    CatalogGridItemColumn.def('contactPhoneExtra', 'Teléfono adicional'),
    CatalogGridItemColumn.def('adddressStreet', 'Calle'),
    CatalogGridItemColumn.def('addressExtNum', 'Número exterior'),
    CatalogGridItemColumn.def('addressIntNum', 'Número interior'),
    CatalogGridItemColumn.def('addressZipcode', 'Código postal'),
    CatalogGridItemColumn.def('addressDistrict', 'Colonia'),
    CatalogGridItemColumn.def('addressDelegation', 'Delegación'),
    CatalogGridItemColumn.def('addressState', 'Estado'),
    CatalogGridItemColumn.def('addressCountry', 'País'),
    CatalogGridItemColumn.def('comercialCharge', 'Comercial a cargo'),
    CatalogGridItemColumn.def('minimumLicenses', 'Mínimo de licencias'),
    CatalogGridItemColumn.def('startDate', 'Inicio de contrato'),
    CatalogGridItemColumn.def('creation', 'Fecha de creación'),
    CatalogGridItemColumn.def('update', 'Fecha de actualización'),
    CatalogGridItemColumn.def('verification', 'Fecha de verificación')
  ]
  items: ClientCatalogGridItem[] = []
  filteredItems: ClientCatalogGridItem[] = []
  filters = new ClientCatalogFilters()
  selectedItem = new ClientCatalogGridItem()
  importClientFields = [
    { key: 'client_code', label: 'Código Cliente' },
    { key: 'name', label: 'Razón Social' }
  ]
  importWorkspacesFields = [
    { key: 'domain', label: 'ID Dominio' },
    { key: 'domain_name', label: 'Dominio' },
    { key: 'client', label: 'ID Cliente' },
    { key: 'client_name', label: 'Cliente' }
  ]

  domains: Domain[] = []
  openWorkspaceCreation = false
  allWorkspaces: Workspace[] = []

  ReportBilling: number = -1

  onDomainCreated: boolean = false
  created () {
    this.searchData()
  }

  async mounted () {
    await this.loadDomains()
  }

  /* User permissions */

  get createPermission () {
    return this.hasRoleWithPermissionToWorkWorkspaces &&
      userInfoStore.hasPermission(PermissionNames.CLIENTS_CREATE)
  }

  get updatePermission () {
    return this.hasRoleWithPermissionToWorkWorkspaces &&
      userInfoStore.hasPermission(PermissionNames.CLIENTS_UPDATE)
  }

  get permissionToChangeWorkspaceStatus () {
    return this.updatePermission &&
      userInfoStore.hasPermission(PermissionNames.WORKSPACES_UPDATE)
  }

  get permissionToDeleteWorkspace () {
    return this.updatePermission &&
      userInfoStore.hasPermission(PermissionNames.WORKSPACES_DELETE)
  }

  get hasRoleWithPermissionToWorkWorkspaces () {
    const allowedRoles = [
      RoleNames.FINANCES_ADMIN,
      RoleNames.FINANCES_CONTROL
    ]
    return allowedRoles.some(role => userInfoStore.hasRole(role))
  }

  /** Import clients */
  get importPermission () {
    return userInfoStore.userInfo.role.permissions.some(it => it.name === PermissionNames.CLIENTS_IMPORT)
  }

  /** Validate if the user has the permissions to create or update workspaces */
  get permissionToWorkWithWorkspaces (): boolean {
    return this.hasPermission(PermissionNames.WORKSPACES_CREATE) ||
      this.hasPermission(PermissionNames.WORKSPACES_UPDATE)
  }

  /**
   * Returns the email property of the userInfo object.
   */
  get userEmail () {
    return userInfoStore.userInfo.email
  }

  /**
   * Checks if the user role has a specific permission
   * @param permission - name to check
   */
  hasPermission (permission: string) {
    return userInfoStore.userInfo.role.permissions.some(it => it.name === permission)
  }

  /** Workspaces import */
  get importWorkspacesPermission () {
    const permissionsToImport = [
      PermissionNames.WORKSPACES_IMPORT,
      PermissionNames.WORKSPACES_CREATE
    ]
    return userInfoStore.hasPermissions(permissionsToImport)
  }

  /* Load data */

  searchData () {
    if (this.loadingData) return
    this.items = []
    this.loadingData = true
    this.cleanFilter()
    this.loadPage(new PageRequest<ClientCatalogFilters>(this.PAGE_SIZE))
    this.getAllWorkspaces(new PageRequest<ClientWorkspacesFilters>(this.PAGE_SIZE))
  }

  async loadPage (pageRequest: PageRequest<any>) {
    pageRequest.sort = 'name'
    const response = await catalogsService.getClients(pageRequest) as any
    this.items.push(...response.results.map((it: Client): ClientCatalogGridItem => {
      return this.fillGridItem(it, new ClientCatalogGridItem())
    }))

    if (this.items.length < response.total) {
      pageRequest.page++
      await this.loadPage(pageRequest)
    } else {
      this.loadingData = false
      this.filteredItems = this.items
    }
  }

  /**
   * Gets all the domain records
   *
   * @param pageRequest with config to the request
   */
  async loadDomainsPage (pageRequest: PageRequest<any>) {
    const response = await catalogsService.getDomains(pageRequest)
    this.domains.push(...response.results)

    if (this.domains.length < response.total) {
      pageRequest.page++
      await this.loadDomainsPage(pageRequest)
    } else {
      this.loadingData = false
    }
  }

  /**
   * Request all the workspaces based on a page request configuration
   */
  async getAllWorkspaces (pageRequest: PageRequest<ClientWorkspacesFilters>) {
    await catalogsService.getWorkspacesAll(pageRequest)
      .then(response => {
        this.allWorkspaces.push(...response.results)

        // Continue loading workspaces
        if (this.allWorkspaces.length < response.total) {
          pageRequest.page++
          this.getAllWorkspaces(pageRequest)
        }
      })
  }

  /**
   * Loads the values for domains records and
   * the domains that does not have any client related.
   * This are needed to relate a client with a domain
   * in the workspaces view.
   */
  async loadDomains () {
    if (this.permissionToWorkWithWorkspaces) {
      await this.loadDomainsPage(new PageRequest<DomainCatalogFilters>(this.PAGE_SIZE))
    }
  }

  /* Grid and action events */

  selectItem (item: any) {
    switch (this.currentAction) {
      case CrudActions.READ:
      case CrudActions.READ_DETAIL:
      case CrudActions.UPDATE:
      case CrudActions.SUSPEND:
      case CrudActions.INACTIVE:
      case CrudActions.ACTIVE:
      case CrudActions.WORKSPACES:
        this.selectedItem = item
        break
      case CrudActions.CREATE:
        this.selectedItem = this.updatePermission ? item : this.selectedItem
        this.currentAction = CrudActions.UPDATE
        break
    }
  }

  selectItemAndShowDetail (item: any) {
    this.selectItem(item)
    this.queryItem()
  }

  isItemSelected (item: any) {
    return this.selectedItem === item
  }

  cleanItemSelected () {
    this.selectedItem = new ClientCatalogGridItem()
  }

  queryItem () {
    if (this.selectedItem.id) {
      this.currentAction = CrudActions.READ_DETAIL
    }
  }

  newItem () {
    this.selectedItem = new ClientCatalogGridItem()
    this.currentAction = CrudActions.CREATE
  }

  updateItem () {
    if (this.selectedItem.id) {
      this.currentAction = CrudActions.UPDATE
    }
  }

  showWorkspacesAdmin () {
    if (this.selectedItem.id) {
      this.currentAction = CrudActions.WORKSPACES
    }
  }

  suspendItem () {
    if (this.selectedItem.id) {
      this.currentAction = CrudActions.SUSPEND
    }
  }

  inactiveItem () {
    if (this.selectedItem.id) {
      this.currentAction = CrudActions.INACTIVE
    }
  }

  activeItem () {
    if (this.selectedItem.id) {
      this.currentAction = CrudActions.ACTIVE
    }
  }

  fillGridItem (origin: Client, target: ClientCatalogGridItem) {
    target.id = origin.id
    target.clientCode = origin.clientCode
    target.name = origin.name
    target.rfc = origin.rfc
    target.status = origin.status
    target.statusName = Client.getStatusDescription(origin.status as ClientStatus) as any
    target.minimumLicenses = origin.minimumLicenses || 0
    target.juridicalPerson = origin.juridicalPerson === 'moral' ? 'Moral' : 'Física'
    target.comercialCharge = origin.comercialCharge || '--'
    target.stpClabe = origin.stpClabe
    target.startDate = origin.startDate ? new DateFormat(origin.startDate).toDateString() : '- - / - - / - - - -'
    target.creation = origin.creationDate ? new DateFormat(origin.creationDate).toDateString() : '- - / - - / - - - -'
    target.update = origin.updateDate ? new DateFormat(origin.updateDate).toDateString() : '- - / - - / - - - -'
    target.verification = origin.verificationDate ? new DateFormat(origin.verificationDate).toDateString() : '- - / - - / - - - -'
    target.contactName = origin.contact.contactName
    target.contactEmail = origin.contact.email
    target.contactEmailExtra = origin.contact.emailExtra
    target.contactPhone = origin.contact.phone
    target.contactPhoneExtra = origin.contact.phoneExtra || '--'
    target.adddressStreet = origin.address.streetAddress
    target.addressExtNum = origin.address.extNumAddress
    target.addressIntNum = origin.address.intNumAddress || '--'
    target.addressDistrict = origin.address.district
    target.addressDelegation = origin.address.delegation
    target.addressState = origin.address.state
    target.addressZipcode = origin.address.zipcode
    target.addressCountry = origin.address.country
    return target
  }

  /* Filter and sorting data */

  cleanFilter () {
    this.resetPaginator()
    this.filters = new ClientCatalogFilters()
    this.filteredItems = this.items
  }

  filterItems () {
    if (this.loadingData) return
    this.loadingData = true
    const regexSearch = new RegExp(this.filters.search, 'i')
    this.resetPaginator()
    this.filteredItems = this.items.filter(it =>
      (!this.filters.search ||
        regexSearch.test(it.rfc) ||
        regexSearch.test(it.name) ||
        regexSearch.test(it.clientCode)
      ) &&
      (!this.filters.status.length || this.filters.status.includes(it.status))
    )
    this.loadingData = false
  }

  isRowVisibleInPage (rowIndex: number) {
    return rowIndex >= this.itemsVisibleRange[0] && rowIndex < this.itemsVisibleRange[1]
  }

  sortItems (col: CatalogGridItemColumn) {
    catalogsService.sortItems(this.filteredItems, col)
  }

  onPageChange (page: number) {
    this.itemsVisibleRange = [(page - 1) * this.PAGE_SIZE_VISIBLE_ITEMS, (page - 1) * this.PAGE_SIZE_VISIBLE_ITEMS + this.PAGE_SIZE_VISIBLE_ITEMS]
    this.cleanItemSelected()
  }

  resetPaginator () {
    this.itemsVisibleRange = [0, this.PAGE_SIZE_VISIBLE_ITEMS]
    this.paginatorKey = new Date().getTime()
    this.cleanItemSelected()
  }

  /* Form */

  onCancelForm () {
    this.currentAction = CrudActions.READ
  }

  async onItemSaved (item: Client) {
    let isNewCient = false
    let isChangedToActive = false
    if (!this.selectedItem.id) {
      isNewCient = true
      let newITem = new ClientCatalogGridItem()
      this.fillGridItem(item, newITem)
      this.items.unshift(newITem)
      this.filterItems()
      this.selectedItem = newITem
    } else {
      if (this.selectedItem.status === ClientStatus.APROBACION && item.status === ClientStatus.ACTIVO) {
        isChangedToActive = true
      }
      this.fillGridItem(item, this.selectedItem)
    }
    this.currentAction = CrudActions.READ

    if (item.status === ClientStatus.ACTIVO && (isNewCient || isChangedToActive)) {
      this.$bvModal.show('add-domain-modal')
    }
  }

  onDomainSaved (item: Domain) {
    this.domains.push(item)
    this.currentAction = CrudActions.READ
    this.showOnSaveFeedback()
  }

  showOnSaveFeedback () {
    this.onDomainCreated = true
    setTimeout(() => {
      this.hideFeedback()
    }, 3000)
  }

  hideFeedback (): void {
    this.onDomainCreated = false
  }

  /**
   * Updates the client status from a determinated action
   *
   * @param status for the client based on its the workspaces status
   * @param action from the action where called this method
   */
  onWorkspaceStatusSaved (status: ClientStatus, action?: string) {
    if (status) {
      this.updateClientStatus(status)
    }
    this.onCancelForm()
    this.$bvToast.toast('La acción se ha completado.',
      {
        title: (action || 'Acción') + ' finalizada.',
        variant: 'success',
        toaster: 'b-toaster-top-right',
        autoHideDelay: 3000,
        solid: true
      })
  }

  /**
   * Update the status of the current client selected
   * with the status that received as parameter
   *
   * @param status to set
   */
  updateClientStatus (status: ClientStatus): void {
    this.selectedItem.status = status
    this.selectedItem.statusName = Client.getStatusDescription(status)
  }

  /* Domain assigment  */

  showDomainAssigment () {
    this.$bvModal.hide('add-domain-modal')
    this.openWorkspaceCreation = true
    this.currentAction = CrudActions.WORKSPACES
  }

  showDomainForm () {
    this.$bvModal.hide('add-domain-modal')
    this.currentAction = CrudActions.DOMAINS
  }

  /* Billing report  */

  showBillingForm () {
    this.$bvModal.show('report-billing')
  }

  closeBillingForm () {
    this.ReportBilling = -1
    this.$bvModal.hide('report-billing')
  }

  /**
   * Sending a request to the server to generate a report.
  */
  requestReport () {
    reportsService
      .postDailyBilling(
        this.userEmail,
        this.ReportBilling
      )
      .then((response) => {
        let message = `En los próximos 20 minutos recibirá un correo con el reporte, cualquier duda por favor comunícate a soporte.`
        let messageValues = {
          title: 'Generando reporte.',
          message: message
        }
        new MessageSuccess({ propsData: messageValues }).displaMessage()
      })
      .catch((error) => {
        let messageValues = {
          title: 'Surgio un error contacte a soporte',
          message: '',
          delay: 10000,
          error: error
        }
        new MessageError({ propsData: messageValues }).displayMessage()
      })
      .finally(() => {
        this.closeBillingForm()
      })
  }

  /* Import data */

  /**
   * Creates a client instance with base on a JSON obect with client values
   * @param client - a JSON object with client values to use
   * @returns newClient - a client instance from Catalogs format
   */
  async prepareClient (client: any) {
    try {
      let newClient = Client.getIntanceForEdit()
      newClient.clientCode = client.hasOwnProperty('client_code') ? client['client_code'].trim() : '' // required
      newClient.name = client.hasOwnProperty('name') ? client['name'].trim() : '' // required
      newClient.rfc = client.hasOwnProperty('rfc') ? client['rfc'].trim() : '' // required
      newClient.juridicalPerson = client.hasOwnProperty('juridical_person') ? client['juridical_person'].trim() : '' // required
      newClient.stpClabe = client.hasOwnProperty('stp_clabe') ? client['stp_clabe'].trim() : ''
      newClient.contact.email = client.hasOwnProperty('email') ? client['email'].trim() : '' // required
      newClient.contact.phone = client.hasOwnProperty('phone') ? client['phone'].trim() : '' // required
      newClient.contact.contactName = client.hasOwnProperty('contact_name') ? client['contact_name'].trim() : '' // required
      newClient.contact.emailExtra = client.hasOwnProperty('email_extra') ? client['email_extra'].trim() : '' // required
      newClient.contact.phoneExtra = client.hasOwnProperty('phone_extra') ? client['phone_extra'].trim() : '' // required
      newClient.status = client.hasOwnProperty('status') ? client['status'].trim() : ''
      newClient.address.streetAddress = client.hasOwnProperty('street_address') ? client['street_address'].trim() : '' // required
      newClient.address.extNumAddress = client.hasOwnProperty('ext_num_address') ? client['ext_num_address'].trim() : '' // required
      newClient.address.intNumAddress = client.hasOwnProperty('int_num_address') ? client['int_num_address'].trim() : ''
      newClient.address.district = client.hasOwnProperty('district') ? client['district'].trim() : '' // required
      newClient.address.delegation = client.hasOwnProperty('delegation_municipality') ? client['delegation_municipality'].trim() : '' // required
      newClient.address.state = client.hasOwnProperty('state') ? client['state'].trim() : '' // required
      newClient.address.zipcode = client.hasOwnProperty('zip_code') ? client['zip_code'].trim() : '' // required
      newClient.address.country = client.hasOwnProperty('country') ? client['country'].trim() : '' // required
      newClient.comercialCharge = client.hasOwnProperty('comercial_charge') ? client['comercial_charge'].trim() : ''
      newClient.minimumLicenses = client.hasOwnProperty('minimun_licenses') ? client['minimun_licenses'] : 0
      newClient.startDate = client.hasOwnProperty('start_date') ? moment(client['start_date'].trim(), 'DD/MM/YYYY').toDate() : moment().toDate()
      if (!newClient.validateRequiredValues()) {
        throw ErrorImport.getErrorRequiredValues(ImportationErrorAdvices.no_required_client_values)
      } else {
        let formatValuesInvalidated = newClient.invalidFormatValues()
        if (formatValuesInvalidated.length) {
          throw ErrorImport.getErrorFormatValues(
            formatValuesInvalidated,
            `${newClient.clientCode} - ${newClient.name}`
          )
        }
      }
      return newClient
    } catch (error) {
      LogService.error('Error durante la preparación de un cliente')
      throw error
    }
  }

  /**
   * Obtains the values to display as details on import file action.
   * This values must be the same specified on 'importClientFields' variable
   * @returns object with column values to use on import file action
   */
  clientDetails (client: Client) {
    return {
      client_code: client.clientCode,
      name: client.name
    }
  }

  /**
   * Gets the save client funtion from catalogService to use in import component
   */
  get saveClient () {
    return catalogsService.saveClient
  }

  /**
   * Searches for a client that matches the client code
   * @param clientCode to match
   */
  findClientByCode (clientCode: string) {
    for (const client of this.items) {
      if (client.clientCode === clientCode) {
        return client as ClientCatalogGridItem
      }
    }
    throw new ErrorImport(
      ImportErrorCode.NO_RELATED_ENTITY,
      ErrorTitles.client_not_found,
      `El cliente con código '${clientCode}' no se ha encontrado.`,
      ImportationErrorAdvices.client_not_found
    )
  }

  /**
   * Creates a workspace instance based on an object with the values to work with
   * @param workspace - object with the values to work with
   * @returns newWorkspace - a workspace instance from Catalogs format
   */
  async prepareWorkspace (workspace: any) {
    try {
      let newWorkspace = Workspace.getIntanceForEdit()
      // we must find the client id based on client code
      let clienteItem = {} as any
      if (workspace.ClientCode) {
        let clientCode: string = String(workspace.ClientCode)
        clienteItem = this.findClientByCode(clientCode.trim())
      }
      // validates the domain existence by its id
      let domainItem = {} as any
      if (workspace.DomainId) {
        domainItem = await catalogsService.getDomainById(workspace.DomainId)
          .catch(() => {
            throw new ErrorImport(
              ImportErrorCode.NO_RELATED_ENTITY,
              ErrorTitles.domain_not_found,
              `El Dominio por el identificador "${workspace.DomainId}" no se ha encontrado`,
              ImportationErrorAdvices.domain_not_found_workspace
            )
          })
      }
      newWorkspace.domain.id = domainItem.id
      newWorkspace.domain.name = domainItem.name
      newWorkspace.client.id = clienteItem.id
      newWorkspace.client.name = clienteItem.clientCode + ' - ' + clienteItem.name
      newWorkspace.owner = workspace.Owner
      newWorkspace.consolidatedInvoice = workspace.ConsolidatedInvoice
      newWorkspace.facturationDate = workspace.FacturationDate
      newWorkspace.contact.name = workspace.ContactName ? String(workspace.ContactName).trim() : ''
      newWorkspace.contact.contactTelephone = workspace.ContactPhone ? String(workspace.ContactPhone).trim() : ''
      newWorkspace.contact.contactEmail = workspace.ContactEmail ? String(workspace.ContactEmail).trim() : ''
      if (!newWorkspace.validateRequiredValues()) {
        throw ErrorImport.getErrorRequiredValues(ImportationErrorAdvices.no_required_workspace_values)
      } else {
        let formatErrors = newWorkspace.validateFormatValues()
        if (formatErrors.length) {
          throw ErrorImport.getErrorFormatValues(
            formatErrors,
            `${newWorkspace.domain.id} - client ${newWorkspace.client.name}`
          )
        }
      }
      return newWorkspace
    } catch (e) {
      LogService.error('Error durante la preparación de un Workspace.')
      throw e
    }
  }

  /**
   * Obtains the values to display as details on import file action.
   * This values must be the same specified on 'importWorkspacesFields' variable
   * @returns Objects with column values to use on import file action
   */
  workspacesDetails (workspace: Workspace) {
    return {
      domain: workspace.domain.id,
      domain_name: workspace.domain.name || '---',
      client: workspace.client.id || 'No encontrado',
      client_name: workspace.client.name || '---'
    }
  }

  /**
   * Gets the save workspace funtion from catalogService to use in import component
   */
  get saveClientWorkspace () {
    return catalogsService.saveWorkspace
  }

  /* Export data */

  printCsv () {
    let csv = 'CODIGO DE CLIENTE,'
    csv += 'ID,'
    csv += 'RAZON SOCIAL,'
    csv += 'ESTATUS,'
    csv += 'RFC,'
    csv += 'PERSONALIDAD JURIDICA,'
    csv += 'CLABE STP,'
    csv += 'NOMBRE CONTACTO,'
    csv += 'CORREO PARA ENVIO DE FACTURAS,'
    csv += 'TELEFONO DE FACTURACION,'
    csv += 'CORREO ADICIONAL,'
    csv += 'TELEFONO ADICIONAL,'
    csv += 'CALLE,'
    csv += 'NUMERO EXTERIOR,'
    csv += 'NUMERO INTERIOR,'
    csv += 'CODIGO POSTAL,'
    csv += 'COLONIA,'
    csv += 'DELEGACION,'
    csv += 'ESTADO,'
    csv += 'PAIS,'
    csv += 'COMERCIAL A CARGO,'
    csv += 'MÍNIMO DE LICENCIAS,'
    csv += 'INICIO DE CONTRATO,'
    csv += 'CREACION,'
    csv += 'ACTUALIZACION,'
    csv += 'VERIFICACION\n'

    for (let it of this.filteredItems) {
      csv += `${it.clientCode},`
      csv += `${it.id},`
      csv += `${reportsService.formatTextForCsvFile(it.name)},`
      csv += `${reportsService.formatTextForCsvFile(it.statusName)},`
      csv += `${it.rfc},`
      csv += `${reportsService.formatTextForCsvFile(it.juridicalPerson)},`
      csv += `${it.stpClabe},`
      csv += `${reportsService.formatTextForCsvFile(it.contactName)},`
      csv += `${it.contactEmail},`
      csv += `${it.contactPhone},`
      csv += `${it.contactEmailExtra},`
      csv += `${it.contactPhoneExtra},`
      csv += `${reportsService.formatTextForCsvFile(it.adddressStreet)},`
      csv += `${it.addressExtNum},`
      csv += `${it.addressIntNum},`
      csv += `${it.addressZipcode},`
      csv += `${reportsService.formatTextForCsvFile(it.addressDistrict)},`
      csv += `${reportsService.formatTextForCsvFile(it.addressDelegation)},`
      csv += `${reportsService.formatTextForCsvFile(it.addressState)},`
      csv += `${reportsService.formatTextForCsvFile(it.addressCountry)},`
      csv += `${it.comercialCharge},`
      csv += `${it.minimumLicenses.toString()},`
      csv += `${it.startDate},`
      csv += `${it.creation},`
      csv += `${it.update},`
      csv += `${it.verification}\n`
    }

    let element = this.$refs.csvDownload as any
    element.href = 'data:text/csv;charset=utf-8,' + encodeURI(csv)
    element.target = '_blank'
    element.download = `clientes.csv`
    element.click()
  }
}
